import { useEffect, useRef, useState } from "react";
import type { EventNode, TypedEvent } from "@/shared/lib/componentHierarchy";
import {
  useComponentNode,
  useHierarchyLifetime,
} from "@/shared/ui/component-hierarchy/hooks";
import {
  createPullRefreshMediator,
  isPullRefreshEvent,
  type PullRefreshEffect,
  type PullRefreshEvent,
  type PullRefreshIndicatorState,
} from "@/shared/ui/feedback/pullRefreshMediator";

export type PullRefreshCallback = () => void | Promise<void>;

export interface PullRefreshPresenterOptions {
  readonly onRefresh?: PullRefreshCallback;
  readonly pullThreshold: number;
  readonly cooldownMs: number;
}

export interface PullRefreshPresentation {
  readonly node: EventNode<TypedEvent>;
  readonly containerRef: React.RefObject<HTMLDivElement | null>;
  readonly topIndicator: PullRefreshIndicatorState | null;
  readonly bottomIndicator: PullRefreshIndicatorState | null;
}

export function usePullRefreshPresenter(
  options: PullRefreshPresenterOptions,
): PullRefreshPresentation {
  const containerRef = useRef<HTMLDivElement>(null);
  const onRefreshRef = useRef(options.onRefresh);
  onRefreshRef.current = options.onRefresh;

  const [topIndicator, setTopIndicator] =
    useState<PullRefreshIndicatorState | null>(null);
  const [bottomIndicator, setBottomIndicator] =
    useState<PullRefreshIndicatorState | null>(null);

  const animationFrameRef = useRef(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const displayTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const indicatorRafRef = useRef(0);
  const pendingTopRef = useRef<PullRefreshIndicatorState | null | undefined>(
    undefined,
  );
  const pendingBottomRef = useRef<PullRefreshIndicatorState | null | undefined>(
    undefined,
  );
  const nativeTouchEventRef = useRef<TouchEvent | null>(null);
  const nodeRef = useRef<EventNode<TypedEvent> | null>(null);
  const mountedRef = useRef(true);
  const clearRuntimeRef = useRef<(clearIndicators: boolean) => void>(
    (): void => undefined,
  );

  const clearIndicatorFrame = (): void => {
    if (indicatorRafRef.current) {
      cancelAnimationFrame(indicatorRafRef.current);
      indicatorRafRef.current = 0;
    }
    pendingTopRef.current = undefined;
    pendingBottomRef.current = undefined;
  };

  const clearRuntime = (clearIndicators: boolean): void => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = 0;
    }
    if (cooldownTimerRef.current !== undefined) {
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = undefined;
    }
    if (displayTimerRef.current !== undefined) {
      clearTimeout(displayTimerRef.current);
      displayTimerRef.current = undefined;
    }
    clearIndicatorFrame();
    nativeTouchEventRef.current = null;
    if (containerRef.current) containerRef.current.style.transform = "";
    if (clearIndicators && mountedRef.current) {
      setTopIndicator(null);
      setBottomIndicator(null);
    }
  };
  clearRuntimeRef.current = clearRuntime;

  const dispatchRuntimeEvent = (event: PullRefreshEvent): void => {
    nodeRef.current?.dispatch(event);
  };

  const scheduleAnimationFrame = (): void => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame((now: number) => {
      animationFrameRef.current = 0;
      dispatchRuntimeEvent({
        type: "pull-refresh/animation-frame",
        now,
      });
    });
  };

  const scheduleIndicator = (
    side: "top" | "bottom",
    state: PullRefreshIndicatorState,
  ): void => {
    if (side === "top") pendingTopRef.current = state;
    else pendingBottomRef.current = state;
    if (indicatorRafRef.current) return;
    indicatorRafRef.current = requestAnimationFrame(() => {
      indicatorRafRef.current = 0;
      const nextTop = pendingTopRef.current;
      const nextBottom = pendingBottomRef.current;
      pendingTopRef.current = undefined;
      pendingBottomRef.current = undefined;
      if (!mountedRef.current) return;
      if (nextTop !== undefined) setTopIndicator(nextTop);
      if (nextBottom !== undefined) setBottomIndicator(nextBottom);
    });
  };

  const effectRunnerRef = useRef<(effect: PullRefreshEffect) => void>(
    (): void => undefined,
  );
  effectRunnerRef.current = (effect: PullRefreshEffect): void => {
    if (!mountedRef.current) return;
    switch (effect.type) {
      case "apply-offset":
        if (containerRef.current) {
          containerRef.current.style.transform =
            effect.offset === 0 ? "" : `translate3d(0, ${effect.offset}px, 0)`;
        }
        return;
      case "schedule-indicator":
        scheduleIndicator(effect.side, effect.state);
        return;
      case "show-refreshing": {
        clearIndicatorFrame();
        const refreshing: PullRefreshIndicatorState = {
          progress: 100,
          refreshing: true,
          opacity: 1,
        };
        if (effect.side === "top") setTopIndicator(refreshing);
        else setBottomIndicator(refreshing);
        return;
      }
      case "clear-indicators":
        clearIndicatorFrame();
        if (mountedRef.current) {
          setTopIndicator(null);
          setBottomIndicator(null);
        }
        return;
      case "prevent-default":
        nativeTouchEventRef.current?.preventDefault();
        return;
      case "start-animation":
        scheduleAnimationFrame();
        return;
      case "request-animation-frame":
        scheduleAnimationFrame();
        return;
      case "invoke-refresh": {
        const callback = onRefreshRef.current;
        if (!callback) {
          dispatchRuntimeEvent({
            type: "pull-refresh/refresh-settled",
            token: effect.token,
            now: performance.now(),
          });
          return;
        }
        Promise.resolve()
          .then(() => callback())
          .catch(() => undefined)
          .finally(() => {
            dispatchRuntimeEvent({
              type: "pull-refresh/refresh-settled",
              token: effect.token,
              now: performance.now(),
            });
          });
        return;
      }
      case "schedule-refresh-display-clear":
        if (displayTimerRef.current !== undefined) {
          clearTimeout(displayTimerRef.current);
        }
        displayTimerRef.current = setTimeout(() => {
          displayTimerRef.current = undefined;
          dispatchRuntimeEvent({
            type: "pull-refresh/refresh-display-elapsed",
            token: effect.token,
          });
        }, effect.delayMs);
        return;
      case "schedule-cooldown":
        if (cooldownTimerRef.current !== undefined) {
          clearTimeout(cooldownTimerRef.current);
        }
        cooldownTimerRef.current = setTimeout(() => {
          cooldownTimerRef.current = undefined;
          dispatchRuntimeEvent({
            type: "pull-refresh/cooldown-elapsed",
            token: effect.token,
          });
        }, effect.delayMs);
        return;
      case "cancel-runtime":
        clearRuntime(true);
        return;
    }
  };

  const [mediator] = useState(() =>
    createPullRefreshMediator({
      pullThreshold: options.pullThreshold,
      cooldownMs: options.cooldownMs,
      runEffect: (effect: PullRefreshEffect): void => {
        effectRunnerRef.current(effect);
      },
    }),
  );
  useHierarchyLifetime(mediator);

  const node = useComponentNode((event) => {
    if (!isPullRefreshEvent(event)) return "unhandled";
    const result = mediator.dispatch(event);
    return result.status === "handled" ? "handled" : "unhandled";
  });
  nodeRef.current = node;

  useEffect(() => {
    const event: PullRefreshEvent = {
      type: "pull-refresh/configuration-changed",
      pullThreshold: options.pullThreshold,
      cooldownMs: options.cooldownMs,
    };
    node.dispatch(event);
  }, [node, options.cooldownMs, options.pullThreshold]);

  useEffect(() => {
    const target = containerRef.current;
    if (!target) return;

    const dispatchNative = (
      event: PullRefreshEvent,
      nativeEvent: TouchEvent,
    ): void => {
      nativeTouchEventRef.current = nativeEvent;
      try {
        node.dispatch(event);
      } finally {
        nativeTouchEventRef.current = null;
      }
    };
    const readClientY = (event: TouchEvent): number =>
      event.touches[0]?.clientY ?? 0;

    const onTouchStart = (event: TouchEvent): void => {
      dispatchNative(
        {
          type: "pull-refresh/touch-start",
          touchCount: event.touches.length,
          clientY: readClientY(event),
        },
        event,
      );
    };
    const onTouchMove = (event: TouchEvent): void => {
      dispatchNative(
        {
          type: "pull-refresh/touch-move",
          touchCount: event.touches.length,
          clientY: readClientY(event),
          scrollY: window.scrollY,
          innerHeight: window.innerHeight,
          scrollHeight: document.documentElement.scrollHeight,
          now: performance.now(),
          canRefresh: onRefreshRef.current !== undefined,
        },
        event,
      );
    };
    const onTouchEnd = (event: TouchEvent): void => {
      dispatchNative(
        {
          type: "pull-refresh/touch-end",
          now: performance.now(),
        },
        event,
      );
    };
    const onTouchCancel = (event: TouchEvent): void => {
      dispatchNative(
        {
          type: "pull-refresh/touch-cancel",
          now: performance.now(),
        },
        event,
      );
    };

    target.addEventListener("touchstart", onTouchStart, { passive: true });
    target.addEventListener("touchmove", onTouchMove, { passive: false });
    target.addEventListener("touchend", onTouchEnd, { passive: true });
    target.addEventListener("touchcancel", onTouchCancel, { passive: true });

    return (): void => {
      target.removeEventListener("touchstart", onTouchStart);
      target.removeEventListener("touchmove", onTouchMove);
      target.removeEventListener("touchend", onTouchEnd);
      target.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [node]);

  useEffect(() => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
      clearRuntimeRef.current(false);
      nodeRef.current = null;
    };
  }, []);

  return {
    node,
    containerRef,
    topIndicator,
    bottomIndicator,
  };
}
